import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Gauge, AlertTriangle, ShieldCheck } from "lucide-react";
import { RoomShell } from "@/components/RoomShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MEDIA_TYPES = ["Advertisement", "Song Lyrics", "TV / Film", "Social Media Post", "News Headline", "Other"];

export default function BrainScan() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ title: "", media_type: "Advertisement", content: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/brain-scan", form);
      setResult(data.result);
      if (data.user) await refresh();
      toast.success("Analysis complete.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const rating = result?.brain_rating ?? 0;
  const ratingColor = rating >= 66 ? "#991B1B" : rating >= 33 ? "#F59E0B" : "#4ade80";

  const field = "w-full rounded-md px-4 py-3 text-[#FDF6E3] outline-none";
  const fieldStyle = { background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" };

  return (
    <RoomShell testid="brain-scan" kicker="Brain Scan Laboratory" title="Scan the Message">
      <p className="text-[#A89A80] max-w-2xl -mt-6 mb-10 leading-relaxed">
        Feed the machine a piece of media — ad copy, lyrics, a headline, a post. The Analyst reveals the hidden
        messages beneath the surface and assigns a Brain Rating. Free to use — signed-in investigators also earn a badge.
      </p>

      <div className="grid lg:grid-cols-2 gap-8">
        <form onSubmit={run} className="space-y-4">
          <input data-testid="scan-title-input" required placeholder="What are we examining? (title)"
            className={field} style={fieldStyle} value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />

          <div className="flex flex-wrap gap-2">
            {MEDIA_TYPES.map((t) => (
              <button key={t} type="button" data-testid={`media-type-${t}`}
                onClick={() => setForm({ ...form, media_type: t })}
                className="rounded-full px-3 py-1.5 font-typewriter text-[10px] uppercase tracking-widest transition-colors"
                style={{
                  background: form.media_type === t ? "#991B1B" : "var(--wood-dark)",
                  color: form.media_type === t ? "#FDF6E3" : "#A89A80",
                  border: "1px solid var(--wood-medium)",
                }}>
                {t}
              </button>
            ))}
          </div>

          <textarea data-testid="scan-content-input" required rows={8} maxLength={2000} placeholder="Paste the content to analyze…"
            className={field} style={fieldStyle} value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })} />
          <p className="text-[10px] text-right font-typewriter uppercase tracking-widest text-[#6b5f4d]">{form.content.length}/2000</p>

          <button data-testid="scan-submit-btn" type="submit" disabled={loading}
            className="w-full rounded-md py-3 font-typewriter text-sm uppercase tracking-[0.25em] transition-colors disabled:opacity-60"
            style={{ background: "#991B1B", color: "#FDF6E3" }}>
            {loading ? "Scanning brainwaves…" : "Run Brain Scan"}
          </button>

          {!user && (
            <p className="text-xs text-[#6b5f4d] text-center">
              No account needed.{" "}
              <button type="button" onClick={() => navigate("/enter")} className="underline text-[#A89A80] hover:text-[#F59E0B]">Sign in</button> to collect badges.
            </p>
          )}
        </form>

        <div>
          {!result && !loading && (
            <div className="rounded-md p-8 h-full flex flex-col items-center justify-center text-center"
              style={{ background: "var(--wood-dark)", border: "1px dashed var(--wood-medium)" }}>
              <Gauge size={40} className="text-[#2C1A0F] mb-4" />
              <p className="font-serif-hq text-xl text-[#A89A80]">The scanner is idle.</p>
              <p className="text-sm text-[#6b5f4d] mt-1">Submit media to reveal its hidden messages.</p>
            </div>
          )}

          {loading && (
            <div className="rounded-md p-8 h-full flex items-center justify-center" style={{ background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" }}>
              <p className="font-typewriter text-sm uppercase tracking-widest text-[#F59E0B] animate-pulse">Analyzing…</p>
            </div>
          )}

          {result && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              data-testid="scan-result"
              className="rounded-md p-6" style={{ background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" }}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-typewriter text-[11px] uppercase tracking-[0.3em] text-[#A89A80]">Brain Rating</p>
                <p className="font-serif-hq text-4xl" style={{ color: ratingColor }} data-testid="scan-rating">{rating}<span className="text-lg text-[#6b5f4d]">/100</span></p>
              </div>
              <div className="h-2 rounded-full overflow-hidden mb-5" style={{ background: "#2C1A0F" }}>
                <div className="h-full" style={{ width: `${rating}%`, background: ratingColor }} />
              </div>

              <p className="font-serif-hq text-2xl text-[#FDF6E3] leading-tight mb-5">"{result.verdict}"</p>

              <p className="font-typewriter text-[11px] uppercase tracking-widest text-[#D97706] mb-2 flex items-center gap-2"><AlertTriangle size={13} /> Hidden Messages</p>
              <ul className="space-y-2 mb-5">
                {(result.hidden_messages || []).map((m, i) => (
                  <li key={i} className="text-sm text-[#E6D5B8] leading-relaxed pl-4 relative">
                    <span className="absolute left-0 text-[#991B1B]">›</span>{m}
                  </li>
                ))}
              </ul>

              {result.techniques?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {result.techniques.map((t, i) => (
                    <span key={i} className="rounded-full px-3 py-1 font-typewriter text-[10px] uppercase tracking-widest text-[#F59E0B]" style={{ border: "1px solid rgba(245,158,11,0.4)" }}>{t}</span>
                  ))}
                </div>
              )}

              <div className="rounded p-4 flex gap-3" style={{ background: "var(--deep-shadow)" }}>
                <ShieldCheck size={18} className="text-[#4ade80] shrink-0 mt-0.5" />
                <p className="text-sm text-[#A89A80] leading-relaxed"><span className="text-[#4ade80] font-typewriter text-[10px] uppercase tracking-widest block mb-1">Antidote</span>{result.antidote}</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </RoomShell>
  );
}
